import { create } from 'zustand';
import {
  ContextTemplate,
  CreateContextTemplatePayload,
  PreviewContextTemplatePayload,
  PreviewContextTemplateResponse,
  UpdateContextTemplatePayload,
} from '@/types/api';
import {
  getContextTemplates as apiGetContextTemplates,
  createContextTemplate as apiCreateContextTemplate,
  updateContextTemplate as apiUpdateContextTemplate,
  deleteContextTemplate as apiDeleteContextTemplate,
  setDefaultInitialContextTemplate as apiSetDefaultInitial,
  setDefaultFollowupContextTemplate as apiSetDefaultFollowup,
  setDefaultCondensedContextTemplate as apiSetDefaultCondensed,
  previewContextTemplate as apiPreviewContextTemplate,
  duplicateContextTemplate as apiDuplicateContextTemplate,
} from '@/lib/api';
import { toast } from 'sonner';

interface ContextTemplateState {
  templates: ContextTemplate[];
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  previewResult: PreviewContextTemplateResponse | null;
  editingTemplateId: string | 'new' | null;
  setEditingTemplateId: (id: string | 'new' | null) => void;
  fetchTemplates: () => Promise<void>;
  createTemplate: (
    payload: CreateContextTemplatePayload,
  ) => Promise<ContextTemplate | null>;
  updateTemplate: (
    id: string,
    payload: UpdateContextTemplatePayload,
  ) => Promise<ContextTemplate | null>;
  deleteTemplate: (id: string) => Promise<void>;
  duplicateTemplate: (id: string) => Promise<void>;
  setDefaultInitial: (id: string) => Promise<void>;
  setDefaultFollowup: (id: string) => Promise<void>;
  setDefaultCondensed: (id: string) => Promise<void>;
  previewTemplate: (payload: PreviewContextTemplatePayload) => Promise<void>;
  clearPreview: () => void;
}

export const useContextMenuTemplateStore = create<ContextTemplateState>(
  (set, get) => ({
    templates: [],
    isLoading: false,
    isInitialized: false,
    error: null,
    previewResult: null,
    editingTemplateId: null,
    setEditingTemplateId: (id) => set({ editingTemplateId: id }),

    fetchTemplates: async () => {
      // Skip if already initialized or currently loading (prevents duplicate fetch in Strict Mode)
      const { isInitialized, isLoading } = get();
      if (isInitialized || isLoading) return;
      
      set({ isLoading: true, error: null });
      try {
        const templates = await apiGetContextTemplates();
        set({ templates, isLoading: false, isInitialized: true });
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to fetch templates.';
        set({ error: errorMsg, isLoading: false });
        toast.error(errorMsg);
      }
    },

    createTemplate: async (payload) => {
      set({ isLoading: true, error: null });
      try {
        const newTemplate = await apiCreateContextTemplate(payload);
        set((state) => ({
          templates: [...state.templates, newTemplate].sort((a, b) =>
            a.template_name.localeCompare(b.template_name),
          ),
          isLoading: false,
        }));
        toast.success('Template created successfully.');
        return newTemplate;
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to create template.';
        set({ error: errorMsg, isLoading: false });
        toast.error(errorMsg);
        return null;
      }
    },

    updateTemplate: async (id, payload) => {
      set({ isLoading: true, error: null });
      try {
        const updatedTemplate = await apiUpdateContextTemplate(id, payload);
        set((state) => ({
          templates: state.templates
            .map((t) => (t.id === id ? updatedTemplate : t))
            .sort((a, b) => a.template_name.localeCompare(b.template_name)),
          isLoading: false,
        }));
        toast.success('Template updated successfully.');
        return updatedTemplate;
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to update template.';
        set({ error: errorMsg, isLoading: false });
        toast.error(errorMsg);
        return null;
      }
    },

    deleteTemplate: async (id: string) => {
      const originalTemplates = get().templates;
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        isLoading: true, // Optimistic update
        error: null,
      }));
      try {
        await apiDeleteContextTemplate(id);
        toast.success('Template deleted successfully.');
        set({ isLoading: false });
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to delete template.';
        set({ error: errorMsg, isLoading: false, templates: originalTemplates }); // Revert on failure
        toast.error(errorMsg);
      }
    },

    duplicateTemplate: async (id: string) => {
      set({ isLoading: true, error: null });
      try {
        const duplicatedTemplate = await apiDuplicateContextTemplate(id);
        set((state) => ({
          templates: [...state.templates, duplicatedTemplate].sort((a, b) =>
            a.template_name.localeCompare(b.template_name),
          ),
          isLoading: false,
        }));
        toast.success(`Template duplicated as "${duplicatedTemplate.template_name}".`);
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to duplicate template.';
        set({ error: errorMsg, isLoading: false });
        toast.error(errorMsg);
      }
    },

    setDefaultInitial: async (id: string) => {
      set({ isLoading: true, error: null });
      try {
        await apiSetDefaultInitial(id);
        set((state) => ({
          templates: state.templates.map((t) => ({
            ...t,
            is_default_initial: t.id === id,
          })),
          isLoading: false,
        }));
        toast.success('Default initial template set.');
      } catch (err) {
        const errorMsg =
          err instanceof Error
            ? err.message
            : 'Failed to set default initial template.';
        set({ isLoading: false, error: errorMsg });
        toast.error(errorMsg);
      }
    },

    setDefaultFollowup: async (id: string) => {
      set({ isLoading: true, error: null });
      try {
        await apiSetDefaultFollowup(id);
        set((state) => ({
          templates: state.templates.map((t) => ({
            ...t,
            is_default_followup: t.id === id,
          })),
          isLoading: false,
        }));
        toast.success('Default follow-up template set.');
      } catch (err) {
        const errorMsg =
          err instanceof Error
            ? err.message
            : 'Failed to set default follow-up template.';
        set({ isLoading: false, error: errorMsg });
        toast.error(errorMsg);
      }
    },

    setDefaultCondensed: async (id: string) => {
      set({ isLoading: true, error: null });
      try {
        await apiSetDefaultCondensed(id);
        set((state) => ({
          templates: state.templates.map((t) => ({
            ...t,
            is_default_condensed: t.id === id,
          })),
          isLoading: false,
        }));
        toast.success('Default condensed context template set.');
      } catch (err) {
        const errorMsg =
          err instanceof Error
            ? err.message
            : 'Failed to set default condensed context template.';
        set({ isLoading: false, error: errorMsg });
        toast.error(errorMsg);
      }
    },

    previewTemplate: async (payload: PreviewContextTemplatePayload) => {
      set({ isLoading: true, error: null });
      try {
        const result = await apiPreviewContextTemplate(payload);
        set({ previewResult: result, isLoading: false });
        toast.success('Preview generated.');
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to generate preview.';
        set({ error: errorMsg, isLoading: false, previewResult: null });
        toast.error(errorMsg);
      }
    },

    clearPreview: () => {
      set({ previewResult: null });
    },
  }),
);